import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import BarcodeReader from 'react-barcode-reader';
import { RegistrationCheckInPanel, actionKey } from '../checkin/RegistrationCheckInPanel';

const buttonClass = 'inline-flex min-h-11 items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-zinc-900';
const secondaryClass = 'inline-flex min-h-11 items-center justify-center rounded-md px-3 py-2 text-sm font-semibold text-zinc-700 ring-1 ring-inset ring-zinc-300 disabled:opacity-50 dark:text-zinc-200 dark:ring-zinc-700';
const inputClass = 'mt-2 block min-h-11 w-full rounded-md bg-white px-3 py-2 text-sm text-zinc-900 ring-1 ring-inset ring-zinc-300 dark:bg-zinc-900 dark:text-white dark:ring-zinc-700';
const request = async (name, data = {}) => {
  const result = await firebase.functions().httpsCallable(`appV2/${name}`)(data);
  if (result.data?.error || result.data?.notFound) {
    throw new Error(result.data.message || 'Registration was not found.');
  }
  return result.data;
};

export default function StaffCheckIn() {
  const { id } = useParams();
  const [events, setEvents] = useState([]);
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkInData, setCheckInData] = useState(null);
  const [busyActionKey, setBusyActionKey] = useState(null);
  const [reference, setReference] = useState('');
  const generation = useRef(0);
  const detail = useRef(null);

  useEffect(() => {
    const current = ++generation.current;
    setLoading(true); setError(''); setEvent(null); setEvents([]);
    setRegistrations([]); setCheckInData(null); setSearch(''); setStatus(''); setPage(0);
    setBusyActionKey(null);
    const load = id ? request('getStaffRegistrations', { eventId: id }) : request('getStaffEvents');
    load.then((result) => {
      if (current !== generation.current) return;
      if (id) { setEvent(result.event); setRegistrations(result.registrations); }
      else setEvents(result);
    }).catch((failure) => {
      if (current === generation.current) setError(failure.message || 'Unable to load check-in.');
    }).finally(() => { if (current === generation.current) setLoading(false); });
    return () => { generation.current += 1; };
  }, [id]);

  const openRegistration = async (value) => {
    if (busyActionKey !== null) return;
    const registrationId = String(value || '').trim().split('/').filter(Boolean).pop();
    if (!registrationId) return;
    const current = generation.current;
    setError(''); setCheckInData(null); setBusyActionKey('loading');
    try {
      const result = await request('getCheckInData', { registrationId });
      if (current !== generation.current) return;
      setCheckInData(result); setReference('');
      detail.current?.focus();
    } catch (failure) {
      if (current === generation.current) setError(failure.message || 'Unable to load registration.');
    } finally { if (current === generation.current) setBusyActionKey(null); }
  };

  const handleAction = async (action) => {
    if (busyActionKey !== null) return;
    const current = generation.current;
    setError(''); setBusyActionKey(actionKey(action));
    try {
      const result = await request('updateCheckIn', action);
      if (current !== generation.current) return;
      setCheckInData(result);
      const attendees = [result.primaryRegistration, result.guestRegistration].filter(Boolean);
      setRegistrations((rows) => rows.map((row) => {
        const updated = attendees.find((attendee) => attendee.documentId === row.id || attendee.id === row.id);
        return updated ? { ...row, status: updated.status } : row;
      }));
    } catch (failure) {
      if (current === generation.current) setError(failure.message || 'Unable to update check-in.');
    } finally { if (current === generation.current) setBusyActionKey(null); }
  };

  const filtered = useMemo(() => registrations.filter((row) =>
    (!status || row.status === status) &&
    [row.firstName, row.lastName, row.guestFirstName, row.guestLastName,
      row.id, row.sessionDate, row.sessionTime, row.sessionItem].join(' ').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`)), [registrations, search, status]);
  const lastPage = Math.max(0, Math.ceil(filtered.length / 25) - 1);
  const currentPage = Math.min(page, lastPage);

  return <div className="mx-auto max-w-6xl">
    <BarcodeReader onScan={openRegistration} onError={() => {}} />
    <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">{event?.name || 'Registration check-in'}</h1>
    <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">{id ? 'Find a registration to check in or undo check-in.' : 'Choose an assigned event to open its registrations.'}</p>
    {error && <p role="alert" className="mt-4 rounded-md bg-red-50 p-4 text-sm text-red-800 dark:bg-red-950 dark:text-red-200">{error}</p>}
    {loading && <p role="status" className="mt-6">Loading…</p>}
    {!loading && !id && <ul className="mt-6 grid gap-3 sm:grid-cols-2">
      {events.map((item) => <li key={item.id}><Link className="block rounded-lg p-5 font-semibold text-zinc-900 ring-1 ring-zinc-200 hover:bg-zinc-50 dark:text-white dark:ring-zinc-700 dark:hover:bg-zinc-900" to={`/manage/registrations/${item.id}`}>{item.name}<span className="mt-2 block text-sm font-normal text-zinc-500 dark:text-zinc-400">Open registrations</span></Link></li>)}
      {!events.length && !error && <li className="text-sm text-zinc-600 dark:text-zinc-300">No active events are assigned to your account.</li>}
    </ul>}
    {!loading && event && <>
      <form className="mt-6 flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); void openRegistration(reference); }}>
        <label className="flex-1 text-sm font-medium" htmlFor="staff-reference">Registration reference<input id="staff-reference" className={inputClass} value={reference} onChange={(e) => setReference(e.target.value)} /></label>
        <button className={buttonClass} disabled={!reference.trim() || busyActionKey !== null}>Open check-in</button>
      </form>
      <section ref={detail} tabIndex={-1} aria-label="Selected registration check-in" className="mt-6">
        {busyActionKey === 'loading' && <p role="status">Loading registration…</p>}
        {checkInData && <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3"><h2 className="text-lg font-semibold">Check-in · {checkInData.eventData.name}</h2><button className={secondaryClass} disabled={busyActionKey !== null} onClick={() => setCheckInData(null)}>Close check-in</button></div>
          <RegistrationCheckInPanel checkInData={checkInData} busyActionKey={busyActionKey} onAction={handleAction} />
        </>}
      </section>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <label className="text-sm font-medium" htmlFor="staff-search">Search registrations<input id="staff-search" className={inputClass} value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} /></label>
        <label className="text-sm font-medium" htmlFor="staff-status">Status<select id="staff-status" className={inputClass} value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}><option value="">All statuses</option>{[...new Set(registrations.map((row) => row.status))].sort().map((value) => <option key={value} value={value}>{value || 'Unknown'}</option>)}</select></label>
      </div>
      <p className="mt-4 text-sm tabular-nums text-zinc-600 dark:text-zinc-300">{filtered.length} registrations</p>
      <div className="mt-3 overflow-x-auto rounded-lg ring-1 ring-zinc-200 dark:ring-zinc-700"><table className="w-full text-left text-sm">
        <thead className="bg-zinc-50 dark:bg-zinc-900"><tr>{['Name', 'Guest', 'Session', 'Status', 'Check-in'].map((label) => <th scope="col" className="px-4 py-3 font-semibold" key={label}>{label}</th>)}</tr></thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">{filtered.slice(currentPage * 25, (currentPage + 1) * 25).map((row) => <tr key={row.id}>
          <td className="px-4 py-3">{row.firstName} {row.lastName}{row.isGuest && <span className="block text-xs text-zinc-500">Guest registration</span>}</td>
          <td className="px-4 py-3">{row.guestFirstName} {row.guestLastName}</td><td className="px-4 py-3">{[row.sessionDate, row.sessionTime, row.sessionItem].filter(Boolean).join(' · ')}</td><td className="px-4 py-3">{row.status}</td>
          <td className="px-4 py-3"><button className={secondaryClass} aria-label={`Open check-in for ${row.firstName} ${row.lastName}`} disabled={busyActionKey !== null} onClick={() => openRegistration(row.id)}>Open check-in</button></td>
        </tr>)}{!filtered.length && <tr><td className="px-4 py-6 text-zinc-500" colSpan={5}>No registrations match.</td></tr>}</tbody>
      </table></div>
      <div className="mt-4 flex items-center justify-between gap-3"><button className={secondaryClass} disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>Previous</button><span className="text-sm tabular-nums">Page {currentPage + 1} of {lastPage + 1}</span><button className={secondaryClass} disabled={currentPage === lastPage} onClick={() => setPage(currentPage + 1)}>Next</button></div>
    </>}
  </div>;
}
